/**
 * Title and message of a Notification Center entry in the user's language.
 *
 * Server-written notifications (cdb_tqs, e.g. the end of a video run) arrive
 * with German `title`/`message`. For the types listed here the text is built
 * from `type` + `data` with DE/EN templates; anything the templates cannot
 * fill falls back to the server's text, and every other type keeps it.
 * The `data` keys each type reads are part of the payload contract
 * (docs/NOTIFICATION_SYSTEM.md).
 */
import type { TFunction } from 'i18next'
import type { Notification } from '@/services/notification-service'
import { notificationActor } from './notificationPayload'

export interface NotificationText {
  title: string
  message: string
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/** The document name: from `data`, else the quoted name in the server title (`Video „x“ verarbeitet`). */
function documentName(notification: Notification): string | undefined {
  const data = notification.data ?? {}
  return (
    str(data.title) ??
    str(data.filename) ??
    str(data.document_title) ??
    str(notification.title?.match(/„([^“]+)“/)?.[1])
  )
}

const BUG_REPORT_STATUSES = ['open', 'in_progress', 'waiting_for_reporter', 'planned', 'resolved', 'closed']
const CLOSE_REASONS = ['done', 'declined', 'duplicate', 'not_a_bug', 'no_response']
const TICKET_TYPES = ['bug', 'feature']

/** `bug_report_reply`: `report_title`, `excerpt` (start of the reply), `actor.name`. */
function bugReportReplyText(t: TFunction, notification: Notification): NotificationText {
  const data = notification.data ?? {}
  const reportTitle = str(data.report_title)
  const excerpt = str(data.excerpt)
  const name = notificationActor(notification)?.name
  const title = reportTitle
    ? t('notifications.events.bugReportReplyTitle', { reportTitle })
    : t('notifications.events.bugReportReplyTitleNoName')
  const message =
    name && excerpt
      ? t('notifications.events.bugReportReplyMessageActorExcerpt', { name, excerpt })
      : (excerpt ?? (name ? t('notifications.events.bugReportReplyMessageActor', { name }) : notification.message))
  return { title, message }
}

/** `bug_report_status_changed`: `report_title`, `status` (the new one), on a
 *  close `close_reason` (#1168). */
function bugReportStatusText(t: TFunction, notification: Notification): NotificationText {
  const data = notification.data ?? {}
  const reportTitle = str(data.report_title)
  const status = str(data.status)
  const reason = str(data.close_reason)
  const title = reportTitle
    ? t('notifications.events.bugReportStatusChangedTitle', { reportTitle })
    : t('notifications.events.bugReportStatusChangedTitleNoName')
  if (!status || !BUG_REPORT_STATUSES.includes(status)) return { title, message: notification.message }
  const statusLabel = t(`notifications.events.bugReportStatus.${status}`)
  const message =
    status === 'closed' && reason && CLOSE_REASONS.includes(reason)
      ? t('notifications.events.bugReportStatusChangedMessageReason', {
          status: statusLabel,
          reason: t(`notifications.events.bugReportCloseReason.${reason}`),
        })
      : t('notifications.events.bugReportStatusChangedMessage', { status: statusLabel })
  return { title, message }
}

/** `bug_report_type_changed` (#1168): `report_title`, `ticket_type` (the new
 *  one), `from_ticket_type`. */
function bugReportTypeText(t: TFunction, notification: Notification): NotificationText {
  const data = notification.data ?? {}
  const reportTitle = str(data.report_title)
  const type = str(data.ticket_type)
  const fromType = str(data.from_ticket_type)
  const title = reportTitle
    ? t('notifications.events.bugReportTypeChangedTitle', { reportTitle })
    : t('notifications.events.bugReportTypeChangedTitleNoName')
  const message =
    type && fromType && TICKET_TYPES.includes(type) && TICKET_TYPES.includes(fromType)
      ? t('notifications.events.bugReportTypeChangedMessage', {
          type: t(`notifications.events.bugReportType.${type}`),
          fromType: t(`notifications.events.bugReportType.${fromType}`),
        })
      : notification.message
  return { title, message }
}

/** `new_bug_report` (BFF, #1091): `report_title`; since #1168 every new
 *  ticket, `ticket_type: 'feature'` reads like a feature request. */
function newBugReportText(t: TFunction, notification: Notification): NotificationText | null {
  const data = notification.data ?? {}
  const title = str(data.report_title) ?? str(data.bugReportTitle)
  if (!title) return null
  if (data.ticket_type === 'feature') {
    return {
      title: t('notifications.events.newFeatureRequestTitle'),
      message: t('notifications.events.newFeatureRequestMessage', { title }),
    }
  }
  return {
    title: t('notifications.events.newBugReportTitle'),
    message: t('notifications.events.newBugReportMessage', { title }),
  }
}

/** `new_feature_request` (BFF, #1091): `request_title`. */
function newFeatureRequestText(t: TFunction, notification: Notification): NotificationText | null {
  const data = notification.data ?? {}
  const title = str(data.request_title) ?? str(data.featureRequestTitle)
  if (!title) return null
  return {
    title: t('notifications.events.newFeatureRequestTitle'),
    message: t('notifications.events.newFeatureRequestMessage', { title }),
  }
}

/** `quota_request` (cdb_tqs, #1091): `scope`; a client budget names `client_name`, a person's request `actor.name`. */
function quotaRequestText(t: TFunction, notification: Notification): NotificationText | null {
  const data = notification.data ?? {}
  if (data.scope === 'client') {
    const client = str(data.client_name)
    if (!client) return null
    return {
      title: t('notifications.events.quotaClientRequestTitle'),
      message: t('notifications.events.quotaClientRequestMessage', { client }),
    }
  }
  const name = notificationActor(notification)?.name
  if (!name) return null
  return {
    title: t('notifications.events.quotaRequestTitle'),
    message: t('notifications.events.quotaRequestMessage', { name }),
  }
}

/** `quota_request_decided` (cdb_tqs, #1091): `approved`. */
function quotaDecidedText(t: TFunction, notification: Notification): NotificationText | null {
  const approved = notification.data?.approved
  if (typeof approved !== 'boolean') return null
  return approved
    ? { title: t('notifications.events.quotaApprovedTitle'), message: t('notifications.events.quotaApprovedMessage') }
    : { title: t('notifications.events.quotaDeclinedTitle'), message: t('notifications.events.quotaDeclinedMessage') }
}

/** `prompt_shared` (cdb_tqs, #1091): `prompt_title`. */
function promptSharedText(t: TFunction, notification: Notification): NotificationText | null {
  const data = notification.data ?? {}
  const promptTitle = str(data.prompt_title) ?? str(data.promptTitle)
  if (!promptTitle) return null
  return {
    title: t('notifications.events.promptSharedTitle'),
    message: t('notifications.events.promptSharedMessage', { promptTitle }),
  }
}

/** `session_assigned` (cdb_tqs, #1091): `session_title`, `deadline_date` (ISO, optional). */
function sessionAssignedText(t: TFunction, notification: Notification, language?: string): NotificationText | null {
  const data = notification.data ?? {}
  const sessionTitle = str(data.session_title) ?? str(data.sessionTitle)
  if (!sessionTitle) return null
  const rawDeadline = str(data.deadline_date) ?? str(data.deadlineDate)
  const deadline = rawDeadline ? new Date(rawDeadline) : null
  const message =
    deadline && !Number.isNaN(deadline.getTime())
      ? t('notifications.events.sessionAssignedMessageWithDeadline', {
          sessionTitle,
          deadline: deadline.toLocaleDateString(language),
        })
      : t('notifications.events.sessionAssignedMessage', { sessionTitle })
  return { title: t('notifications.events.sessionAssignedTitle'), message }
}

/** `review_tasks_created` (cdb_tqs#1975): `count`, `document_title`. One per
 *  processed document; the target opens its review tasks in the Verifier. */
function reviewTasksText(t: TFunction, notification: Notification): NotificationText | null {
  const data = notification.data ?? {}
  const count = typeof data.count === 'number' ? data.count : Number(data.count)
  const documentTitle = str(data.document_title)
  if (!documentTitle || !Number.isFinite(count) || count < 1) return null
  return {
    title: t('notifications.events.reviewTasksTitle'),
    message: t('notifications.events.reviewTasksMessage', { count, documentTitle }),
  }
}

/** `recording_transcription_*`: the recording's name is the quoted one in the server title. */
function recordingTranscriptionText(t: TFunction, notification: Notification): NotificationText {
  const name = documentName(notification)
  const state = notification.type === 'recording_transcription_completed' ? 'Completed' : 'Failed'
  return {
    title: name
      ? t(`notifications.events.transcription${state}Title`, { name })
      : t(`notifications.events.transcription${state}TitleNoName`),
    message: t(`notifications.events.transcription${state}Message`),
  }
}

const TEXT_BY_TYPE: Partial<
  Record<Notification['type'], (t: TFunction, n: Notification, language?: string) => NotificationText | null>
> = {
  recording_transcription_completed: recordingTranscriptionText,
  recording_transcription_failed: recordingTranscriptionText,
  bug_report_reply: bugReportReplyText,
  bug_report_status_changed: bugReportStatusText,
  bug_report_type_changed: bugReportTypeText,
  new_bug_report: newBugReportText,
  new_feature_request: newFeatureRequestText,
  quota_request: quotaRequestText,
  quota_request_decided: quotaDecidedText,
  prompt_shared: promptSharedText,
  session_assigned: sessionAssignedText,
  review_tasks_created: reviewTasksText,
}

/**
 * `language` formats dates (e.g. a session deadline); without it the
 * browser's locale applies.
 */
export function notificationText(t: TFunction, notification: Notification, language?: string): NotificationText {
  const fallback = { title: notification.title, message: notification.message }
  const render = TEXT_BY_TYPE[notification.type]
  if (render) return render(t, notification, language) ?? fallback
  if (notification.type !== 'document_processing_completed' && notification.type !== 'document_processing_failed') {
    return fallback
  }
  const data = notification.data ?? {}
  const noun = t(data.media_type === 'video' ? 'notifications.events.processingNounVideo' : 'notifications.events.processingNounDocument')
  const name = documentName(notification)
  const completed = notification.type === 'document_processing_completed'
  const title = name
    ? t(completed ? 'notifications.events.processingCompletedTitle' : 'notifications.events.processingFailedTitle', { noun, name })
    : t(completed ? 'notifications.events.processingCompletedTitleNoName' : 'notifications.events.processingFailedTitleNoName', {
        noun,
      })
  const knowledgeArea = str(data.knowledge_area)
  // A failure's message is the server's reason (free text); keep it.
  const message = completed
    ? knowledgeArea
      ? t('notifications.events.processingCompletedMessage', { knowledgeArea })
      : notification.message
    : notification.message || t('notifications.events.processingFailedMessage')
  return { title, message }
}
